import React from 'react';
import PropTypes from 'prop-types';
import {
  Clock1,
  Clock2,
  Clock3,
  Clock4,
  Clock5,
  Clock6,
  Clock7,
  Clock8,
  Clock9,
  Clock10,
  Clock11,
  Clock12,
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

/**
 * TimeChip Component
 *
 * Displays a clock icon relative to how far back the timestamp was,
 * along with a formatted date string and relative time.
 *
 * @param {Object} props
 * @param {number} props.timestamp - Unix timestamp in seconds
 * @param {Function} [props.onClick] - Optional click handler
 * @param {string} [props.className] - Additional CSS classes for the button
 * @param {string} [props.avatarClassName] - Additional CSS classes for the avatar
 * @param {number} [props.size=28] - Size of the avatar
 * @param {string} [props.shape='circle'] - Shape of the avatar
 */
const TimeChip = ({
  timestamp,
  onClick,
  className = '',
  avatarClassName = 'bg-transparent',
  size = 28,
  shape = 'circle',
  showIcon = true,
}) => {
  const currentTime = Date.now() / 1000; // Current time in seconds
  const diffSeconds = currentTime - timestamp;

  // Define time thresholds in seconds (up to 1 year)
  const secondsInMonth = 60 * 60 * 24 * 30;
  const secondsInYear = 60 * 60 * 24 * 365;

  // Array of clock icons from Clock12 (recent) to Clock1 (oldest within a year)
  const clockIcons = [
    Clock12, // 0-1 month
    Clock11, // 1-2 months
    Clock10, // 2-3 months
    Clock9,  // 3-4 months
    Clock8,  // 4-5 months
    Clock7,  // 5-6 months
    Clock6,  // 6-7 months
    Clock5,  // 7-8 months
    Clock4,  // 8-9 months
    Clock3,  // 9-10 months
    Clock2,  // 10-11 months
    Clock1,  // 11-12 months
  ];

  // Determine which clock icon to use based on the time difference
  let ClockIconComponent = Clock12; // Default to most recent

  if (diffSeconds < 0) {
    // Future timestamp: treat as most recent
    ClockIconComponent = Clock12;
  } else if (diffSeconds < secondsInYear) {
    // Within 1 year: determine the month range
    const monthsPassed = Math.floor(diffSeconds / secondsInMonth);
    // Clamp the value between 0 and 11
    const index = Math.min(monthsPassed, 11);
    ClockIconComponent = clockIcons[index];
  } else {
    // Over 1 year: use Clock1 or another icon to represent older dates
    ClockIconComponent = Clock1;
  }

  // Select the appropriate Clock Icon
  const SelectedClockIcon = ClockIconComponent;

  // Format the relative time (e.g., "3 hours ago")
  const relativeTime = formatDistanceToNow(new Date(timestamp * 1000), {
    addSuffix: true,
  });

  // Format the date as "Jan 6, 2022"
  const date = new Date(timestamp * 1000);
  const formattedDate = date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

  return (
    <div
      className={`flex items-center ${className}`}
      onClick={onClick}
      style={{ cursor: onClick ? 'pointer' : 'default' }}
    >
      {showIcon && (
        <div className="flex-shrink-0">
          <SelectedClockIcon size={size * 0.7} color={'#475569'} />
        </div>
      )}
      <div className="ml-2" style={{display:'flex', flexDirection:'column'}}>
        <span className={'font-normal text-gray-700 text-xs'}>{formattedDate}</span>
        <span className={'capitalize text-gray-700'} style={{fontSize: 10}}>{relativeTime}</span>
      </div>
    </div>
  );
};

// PropTypes for type checking and better developer experience
TimeChip.propTypes = {
  timestamp: PropTypes.number.isRequired,
  onClick: PropTypes.func,
  className: PropTypes.string,
  avatarClassName: PropTypes.string,
  size: PropTypes.number,
  shape: PropTypes.string,
  showIcon: PropTypes.bool,
};

export default TimeChip;
